import axios from "axios";
import { apiClient, handleUnauthorized } from './apiClient';
import { API_CONFIG } from "../utils/constants";

// Drops undefined/null/"" keys so apiClient.get's URLSearchParams doesn't
// serialize them as the literal string "undefined" in the query string.
const stripEmpty = (params: Record<string, any>): Record<string, any> => {
    const result: Record<string, any> = {};
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            result[key] = value;
        }
    });
    return result;
};

// Admin list endpoints aren't all response_model-typed in the OpenAPI schema,
// and the ones that are confirmed don't share one convention — campaign/list
// returns {data:[...], total, total_pages}, while the /logs/* endpoints
// confirmed return {success, count, logs} with NO total across pages (count
// is just this page's row count). This normalizes both (plus a couple of
// other plausible shapes) into one consistent form, and flags whether the
// "total" is a real cross-page total or just a same-page count — the latter
// can't back a numbered pager, only a "was this page full" Prev/Next.
export const normalizeListResponse = (
    res: any
): { items: any[]; total: number; totalPages: number; hasReliableTotal: boolean } => {
    const body = res?.data ?? {};
    const container = body?.data ?? body;

    const items = Array.isArray(container)
        ? container
        : container?.items ?? container?.users ?? container?.logs ?? container?.results ?? [];

    const itemCount = Array.isArray(items) ? items.length : 0;

    const realTotal = body?.total ?? container?.total;
    const hasReliableTotal = realTotal !== undefined && realTotal !== null;

    const total = hasReliableTotal ? realTotal : body?.count ?? container?.count ?? itemCount;
    const totalPages = hasReliableTotal
        ? body?.total_pages ?? container?.total_pages ?? Math.max(1, Math.ceil(total / 10))
        : 1;

    return { items: Array.isArray(items) ? items : [], total, totalPages, hasReliableTotal };
};

export const commonService = {
    getAllAdsPowerUsers: async (): Promise<any> => {
        try {
            return await apiClient.get<any>(API_CONFIG.ENDPOINTS.ADSPOWER_GET_ALL);
        } catch (error) {
            console.error("Error fetching campaign details:", error);
            throw error;
        }
    },
    dashboard: async (data: any): Promise<any> => {
        try {
            return await apiClient.get<any>(API_CONFIG.ENDPOINTS.DASHBOARD, {
                params: data
            });
        } catch (error) {
            console.error("Error fetching campaign details:", error);
            throw error;
        }
    },
    updateAdsPowerUser: async (id: any, data: any): Promise<any> => {
        try {
            return await apiClient.put<any>(
                `${API_CONFIG.ENDPOINTS.ADSPOWER_UPDATE}/${id}`,
                data
            );
        } catch (error) {
            console.error("Error fetching campaign details:", error);
            throw error;
        }
    },
    createAdsPowerUser: async (data: any): Promise<any> => {
        try {
            return await apiClient.post<any>(API_CONFIG.ENDPOINTS.ADSPOWER_CREATE, data);
        } catch (error) {
            console.error("Error fetching campaign details:", error);
            throw error;
        }
    },
    deleteAdsPowerUser: async (user_id: any): Promise<any> => {
        try {
            return await apiClient.delete<any>(
                `${API_CONFIG.ENDPOINTS.ADSPOWER_DELETE}/${user_id}`
            );
        } catch (error) {
            console.error("Error fetching campaign details:", error);
            throw error;
        }
    },
    launchSequenceStep: async (id: any, salesforceAccount?: number): Promise<any> => {
        try {
            const params = salesforceAccount
                ? { salesforce_account: salesforceAccount }
                : undefined;

            return await apiClient.get<any>(`${API_CONFIG.ENDPOINTS.LAUNCH_SEQUENCE}/${id}`, params);
        } catch (error) {
            console.error("Error fetching campaign details:", error);
            throw error;
        }
    },
    getSalesforceAccounts: async (): Promise<any> => {
        try {
            return await apiClient.get<any>(API_CONFIG.ENDPOINTS.SALESFORCE_ACCOUNTS);
        } catch (error) {
            console.error("Error fetching Salesforce accounts:", error);
            throw error;
        }
    },
    retryLeadScrape: async (leadId: any): Promise<any> => {
        try {
            return await apiClient.post<any>(
                `${API_CONFIG.ENDPOINTS.RETRY_LEAD_SCRAPE}/${leadId}/retry-scrape`
            );
        } catch (error) {
            console.error("Error retrying lead scrape:", error);
            throw error;
        }
    },
    retryEmailVerification: async (emailLinkId: any): Promise<any> => {
        try {
            return await apiClient.post<any>(
                `${API_CONFIG.ENDPOINTS.RETRY_EMAIL_VERIFICATION}/${emailLinkId}/retry`
            );
        } catch (error) {
            console.error("Error retrying email verification:", error);
            throw error;
        }
    },
    updateSequenceSender: async (
        sequenceId: any,
        data: { new_name: string; new_email: string }
    ): Promise<any> => {
        try {
            // Confirmed against the backend route handler: the sender endpoint
            // takes UpdateSenderSchema as a JSON body (unlike reselect-account,
            // whose salesforce_account is a bare scalar param -> query string).
            return await apiClient.put<any>(
                `${API_CONFIG.ENDPOINTS.UPDATE_SEQUENCE_SENDER}/${sequenceId}/sender`,
                data
            );
        } catch (error) {
            console.error("Error updating sequence sender:", error);
            throw error;
        }
    },
    reselectSalesforceAccount: async (
        sequenceId: any,
        salesforceAccount: number
    ): Promise<any> => {
        try {
            return await apiClient.post<any>(
                `${API_CONFIG.ENDPOINTS.RESELECT_SALESFORCE_ACCOUNT}/${sequenceId}/reselect-account?salesforce_account=${salesforceAccount}`
            );
        } catch (error) {
            console.error("Error reselecting Salesforce account:", error);
            throw error;
        }
    },
    rescheduleSequence: async (sequenceId: any): Promise<any> => {
        try {
            return await apiClient.post<any>(
                `${API_CONFIG.ENDPOINTS.RESCHEDULE_SEQUENCE}/${sequenceId}/reschedule`
            );
        } catch (error) {
            console.error("Error rescheduling sequence:", error);
            throw error;
        }
    },
    downloadCampaignEmails: async (campaignId: any): Promise<void> => {
        const token = localStorage.getItem("accessToken");

        let response: Response;

        try {
            response = await fetch(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CAMPAIGN_DETAIL}/${campaignId}/emails/download`,
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                }
            );
        } catch (error) {
            console.error("Error downloading campaign emails:", error);
            throw error;
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));

            if (response.status === 401) {
                handleUnauthorized();
            }

            throw { message: response.statusText, status: response.status, response: errorData };
        }

        const blob = await response.blob();

        const disposition = response.headers.get("Content-Disposition") || "";
        const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
        const filename = filenameMatch?.[1] || `campaign_${campaignId}_emails.csv`;

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
    },
    downloadCampaignImportFile: async (campaignId: any): Promise<void> => {
        const token = localStorage.getItem("accessToken");

        let response: Response;

        try {
            response = await fetch(
                `${API_CONFIG.BASE_URL}campaign/${campaignId}/download-import`,
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                }
            );
        } catch (error) {
            console.error("Error downloading campaign import file:", error);
            throw error;
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));

            if (response.status === 401) {
                handleUnauthorized();
            }

            throw { message: response.statusText, status: response.status, response: errorData };
        }

        const blob = await response.blob();

        const disposition = response.headers.get("Content-Disposition") || "";
        const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
        const filename = filenameMatch?.[1] || `campaign_${campaignId}_import.csv`;

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
    },
    listCampaignRawHtml: async (campaignId: any): Promise<any> => {
        try {
            return await apiClient.get<any>(`campaign/${campaignId}/raw-html`);
        } catch (error) {
            console.error("Error listing campaign raw HTML files:", error);
            throw error;
        }
    },
    downloadCampaignRawHtml: async (campaignId: any, filename: string): Promise<void> => {
        const token = localStorage.getItem("accessToken");

        let response: Response;

        try {
            response = await fetch(
                `${API_CONFIG.BASE_URL}campaign/${campaignId}/raw-html/${encodeURIComponent(filename)}`,
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                }
            );
        } catch (error) {
            console.error("Error downloading campaign raw HTML file:", error);
            throw error;
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));

            if (response.status === 401) {
                handleUnauthorized();
            }

            throw { message: response.statusText, status: response.status, response: errorData };
        }

        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
    },
    // Same endpoint/auth as downloadCampaignRawHtml above - fetched as a
    // blob (a plain <a>/window.open navigation to the raw URL can't carry
    // the Bearer token) and opened in a new tab instead of saved, so the
    // admin can inspect the complete saved file exactly as stored, unaltered.
    viewCampaignRawHtml: async (campaignId: any, filename: string): Promise<void> => {
        const token = localStorage.getItem("accessToken");

        let response: Response;

        try {
            response = await fetch(
                `${API_CONFIG.BASE_URL}campaign/${campaignId}/raw-html/${encodeURIComponent(filename)}`,
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                }
            );
        } catch (error) {
            console.error("Error viewing campaign raw HTML file:", error);
            throw error;
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));

            if (response.status === 401) {
                handleUnauthorized();
            }

            throw { message: response.statusText, status: response.status, response: errorData };
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        // Intentionally not revoked here - the new tab needs the object
        // URL to remain valid while it renders; the browser reclaims it
        // when that tab/document is later closed/unloaded.
        window.open(url, "_blank");
    },
    getCampaignDetails: async (id: any): Promise<any> => {
        try {
            return await apiClient.get<any>(`${API_CONFIG.ENDPOINTS.CAMPAIGN_DETAIL}/${id}`);
        } catch (error) {
            console.error("Error fetching campaign details:", error);
            throw error;
        }
    },
    updateSequenceTemplate: async (data: any): Promise<any> => {
        try {
            return await apiClient.put<any>(`${API_CONFIG.ENDPOINTS.UPDATE_EMAIL_TEMPLATE}`, data);
        } catch (error) {
            console.error("Error updating sequence template:", error);
            throw error;
        }
    },
    getSequence: async (id: any): Promise<any> => {
        try {
            return await apiClient.get<any>(`${API_CONFIG.ENDPOINTS.SEQUENCE}/${id}`);
        } catch (error) {
            console.error("Error fetching sequence:", error);
            throw error;
        }
    },

    getStrategy: async (id: any): Promise<any> => {
        try {
            return await apiClient.get<any>(`${API_CONFIG.ENDPOINTS.STRATEGY}/${id}`);
        } catch (error) {
            console.error("Error fetching strategy:", error);
            throw error;
        }
    },

    strategy: async (data: any): Promise<any> => {
        try {
            return await apiClient.post<any>(API_CONFIG.ENDPOINTS.STRATEGY, data);
        } catch (error) {
            console.error("Error in strategy service:", error);
            throw error;
        }
    },
    campaignList: async (data: any): Promise<any> => {
        try {
            return await apiClient.get<any>(API_CONFIG.ENDPOINTS.CAMPAIGN_LIST, data);
        } catch (error) {
            console.error("Error fetching campaign list:", error);
            throw error;
        }
    },
    upload: async (
        data: FormData,
        onUploadProgress?: (progress: number) => void
    ): Promise<any> => {
        try {
            const token = localStorage.getItem("accessToken");

            return await axios.post(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.UPLOAD_CAMPAIGN}`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        // ❌ DO NOT set Content-Type manually
                    },
                    onUploadProgress: (event) => {
                        if (event.total && onUploadProgress) {
                            const percent = Math.round(
                                (event.loaded * 100) / event.total
                            );
                            onUploadProgress(percent);
                        }
                    },
                }
            );
        } catch (error: any) {
            if (error?.response?.status === 401) {
                handleUnauthorized();
            }
            throw error;
        }
    },
    // services/otherServices.ts

    emailUpdateSocket: (
        payload: {
            new_name: string;
            new_email: string;
            sequence_id?: number;
            salesforce_account?: number;
        },
        callbacks: {
            onLog: (message: string) => void;
            onCompleted: (success: boolean) => void;
            onError: (message: string) => void;
        }
    ) => {

        try {
            const token = localStorage.getItem("accessToken");

            if (!token) {
                callbacks.onError("No authentication token found. Please log in again.");
                return;
            }

            const WS_BASE_URL =
                API_CONFIG.BASE_URL
                    .replace("https://", "wss://")
                    .replace("http://", "ws://");

            const socket =
                new WebSocket(
                    `${WS_BASE_URL}${API_CONFIG.ENDPOINTS.WS_EMAIL_UPDATE}?token=${encodeURIComponent(token)}`
                );

            socket.onopen = () => {

                socket.send(
                    JSON.stringify(payload)
                );
            };

            socket.onmessage = (
                event
            ) => {

                const data =
                    JSON.parse(event.data);

                // logs
                if (
                    data.type === "log"
                ) {

                    callbacks.onLog(
                        data.message
                    );
                }

                // completed
                if (
                    data.type === "completed"
                ) {

                    callbacks.onCompleted(
                        data.success
                    );

                    socket.close();
                }

                // backend error
                if (
                    data.type === "error"
                ) {

                    callbacks.onError(
                        data.message
                    );

                    socket.close();
                }
            };

            socket.onerror = () => {

                callbacks.onError(
                    "WebSocket connection failed"
                );

                socket.close();
            };

            return socket;

        } catch (error) {

            console.error(
                "Email websocket error:",
                error
            );

            callbacks.onError(
                "Failed to connect websocket"
            );
        }
    },

    // Live campaign progress + activity-log feed, backed by the backend's
    // GET /campaign/{id}/progress/stream (Server-Sent Events). Uses fetch +
    // a manual "data: ...\n\n" reader instead of the native EventSource API
    // so the existing Bearer-token Authorization header can be reused —
    // EventSource can't set custom headers, and unlike emailUpdateSocket's
    // WebSocket (which has the same limitation), there's no need to fall
    // back to a token-in-URL for this one.
    // Returns an unsubscribe function; call it on unmount/campaign change.
    subscribeCampaignProgress: (
        campaignMasterId: any,
        callbacks: {
            onUpdate: (payload: {
                campaign_status: string;
                processing_status: any;
                new_logs: any[];
                server_time: string;
            }) => void;
            onError?: (message: string) => void;
        }
    ): (() => void) => {

        const controller = new AbortController();

        (async () => {

            try {
                const token = localStorage.getItem("accessToken");

                const response = await fetch(
                    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CAMPAIGN_PROGRESS_STREAM}/${campaignMasterId}/progress/stream`,
                    {
                        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                        signal: controller.signal,
                    }
                );

                if (!response.ok || !response.body) {
                    callbacks.onError?.(`Failed to open progress stream (${response.status})`);
                    return;
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = "";

                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });

                    let separatorIndex;
                    while ((separatorIndex = buffer.indexOf("\n\n")) !== -1) {
                        const rawEvent = buffer.slice(0, separatorIndex);
                        buffer = buffer.slice(separatorIndex + 2);

                        const dataLine = rawEvent
                            .split("\n")
                            .find((line) => line.startsWith("data:"));

                        if (!dataLine) continue;

                        try {
                            callbacks.onUpdate(JSON.parse(dataLine.slice(5).trim()));
                        } catch {
                            // Malformed frame — skip it, the next tick will resync.
                        }
                    }
                }

            } catch (err: any) {

                if (err?.name !== "AbortError") {
                    callbacks.onError?.(err?.message || "Progress stream disconnected");
                }
            }
        })();

        return () => controller.abort();
    },

    // ==========================================
    // ADMIN — USER MANAGEMENT
    // ==========================================

    getUsers: async (params: { search?: string; page?: number; limit?: number }): Promise<any> => {
        try {
            return await apiClient.get<any>(API_CONFIG.ENDPOINTS.ADMIN_USERS, stripEmpty(params));
        } catch (error) {
            console.error("Error fetching users:", error);
            throw error;
        }
    },
    createUser: async (data: {
        fname: string;
        email: string;
        mobile: string;
        role: string;
        password: string;
        created_by?: string;
    }): Promise<any> => {
        try {
            return await apiClient.post<any>(API_CONFIG.ENDPOINTS.REGISTER, data);
        } catch (error) {
            console.error("Error creating user:", error);
            throw error;
        }
    },
    updateUser: async (
        userId: any,
        data: { fname: string; email: string; mobile: string; role: string }
    ): Promise<any> => {
        try {
            return await apiClient.put<any>(`${API_CONFIG.ENDPOINTS.ADMIN_USERS}/${userId}`, data);
        } catch (error) {
            console.error("Error updating user:", error);
            throw error;
        }
    },
    deleteUser: async (userId: any): Promise<any> => {
        try {
            return await apiClient.delete<any>(`${API_CONFIG.ENDPOINTS.ADMIN_USERS}/${userId}`);
        } catch (error) {
            console.error("Error deleting user:", error);
            throw error;
        }
    },
    activateUser: async (userId: any): Promise<any> => {
        try {
            return await apiClient.put<any>(`${API_CONFIG.ENDPOINTS.ADMIN_USERS}/${userId}/activate`);
        } catch (error) {
            console.error("Error activating user:", error);
            throw error;
        }
    },
    deactivateUser: async (userId: any): Promise<any> => {
        try {
            return await apiClient.put<any>(`${API_CONFIG.ENDPOINTS.ADMIN_USERS}/${userId}/deactivate`);
        } catch (error) {
            console.error("Error deactivating user:", error);
            throw error;
        }
    },

    // ==========================================
    // ADMIN — ACTIVITY LOGS
    // ==========================================

    searchLogs: async (params: {
        category?: string;
        event_type?: string;
        status?: string;
        campaign_master_id?: number;
        lead_id?: number;
        actor_email?: string;
        company_domain?: string;
        request_id?: string;
        date_from?: string;
        date_to?: string;
        page?: number;
        limit?: number;
    }): Promise<any> => {
        try {
            return await apiClient.get<any>(API_CONFIG.ENDPOINTS.LOGS_SEARCH, stripEmpty(params));
        } catch (error) {
            console.error("Error searching logs:", error);
            throw error;
        }
    },
    logsForDomain: async (domain: string): Promise<any> => {
        try {
            return await apiClient.get<any>(API_CONFIG.ENDPOINTS.LOGS_DOMAIN, { domain });
        } catch (error) {
            console.error("Error fetching domain logs:", error);
            throw error;
        }
    },
    logsForUser: async (email: string): Promise<any> => {
        try {
            return await apiClient.get<any>(API_CONFIG.ENDPOINTS.LOGS_USER, { email });
        } catch (error) {
            console.error("Error fetching user logs:", error);
            throw error;
        }
    },
    logsForCampaign: async (campaignMasterId: any): Promise<any> => {
        try {
            return await apiClient.get<any>(`${API_CONFIG.ENDPOINTS.LOGS_CAMPAIGN}/${campaignMasterId}`);
        } catch (error) {
            console.error("Error fetching campaign logs:", error);
            throw error;
        }
    },

    // ==========================================
    // ADMIN — TEST SEND (real Salesforce send, no scraping/verification)
    // ==========================================

    testSendCampaign: async (data: {
        sender_name: string;
        sender_email: string;
        receiver_emails: string[];
        subject: string;
        body: string;
        salesforce_account: number;
    }): Promise<any> => {
        try {
            return await apiClient.post<any>(API_CONFIG.ENDPOINTS.TEST_SEND_CAMPAIGN, data);
        } catch (error) {
            console.error("Error running test send:", error);
            throw error;
        }
    },
    confirmTestSend: async (sequenceId: any): Promise<any> => {
        try {
            return await apiClient.post<any>(`${API_CONFIG.ENDPOINTS.TEST_SEND_CAMPAIGN}/${sequenceId}/confirm`);
        } catch (error) {
            console.error("Error confirming test send:", error);
            throw error;
        }
    },
    abandonTestSend: async (sequenceId: any): Promise<any> => {
        try {
            return await apiClient.post<any>(`${API_CONFIG.ENDPOINTS.TEST_SEND_CAMPAIGN}/${sequenceId}/abandon`);
        } catch (error) {
            console.error("Error abandoning test send:", error);
            throw error;
        }
    },

    // ==========================================
    // ADMIN — SALESFORCE SEND METHOD (global switch: automation vs API)
    // ==========================================

    getSalesforceSendMethod: async (): Promise<any> => {
        try {
            return await apiClient.get<any>(API_CONFIG.ENDPOINTS.SALESFORCE_SEND_METHOD);
        } catch (error) {
            console.error("Error fetching Salesforce send method:", error);
            throw error;
        }
    },
    updateSalesforceSendMethod: async (sendMethod: "automation" | "api"): Promise<any> => {
        try {
            return await apiClient.put<any>(API_CONFIG.ENDPOINTS.SALESFORCE_SEND_METHOD, {
                send_method: sendMethod,
            });
        } catch (error) {
            console.error("Error updating Salesforce send method:", error);
            throw error;
        }
    },
    updateSenderEmailApi: async (data: {
        account: number;
        display_name: string;
        email: string;
    }): Promise<any> => {
        try {
            return await apiClient.patch<any>(
                `${API_CONFIG.ENDPOINTS.SALESFORCE_API_SENDER_EMAIL}?account=${data.account}`,
                { display_name: data.display_name, email: data.email }
            );
        } catch (error) {
            console.error("Error updating sender email via API:", error);
            throw error;
        }
    },
    getSenderEmailApiStatus: async (data: {
        account: number;
        display_name: string;
        email: string;
    }): Promise<any> => {
        try {
            const params = new URLSearchParams({
                account: String(data.account),
                display_name: data.display_name,
                email: data.email,
            });
            return await apiClient.get<any>(
                `${API_CONFIG.ENDPOINTS.SALESFORCE_API_SENDER_EMAIL_STATUS}?${params.toString()}`
            );
        } catch (error) {
            console.error("Error checking sender email API status:", error);
            throw error;
        }
    },
};

export default commonService;